import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'
import { sendToApi } from 'api/client'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import qs from 'qs'
import { getNextPageParam } from 'utils/getNextPageParam'
import { Response } from 'request-dot-js/index'
import { reportError } from 'utils/error'
import { IMediaFile as BaseMediaFile } from 'components/Design/UploadsTab/Types'
import { AlmanacFile } from 'types'

export interface IMediaFile extends BaseMediaFile, Required<Pick<AlmanacFile, 'media_file_id'>> {}

interface Data {
  media_files: IMediaFile[]
}

interface Params {
  page: number
  items: number
  order_by?: 'recent' | undefined
  mime_types?: string
  media_source?: 'website' | 'brand_kit'
}

interface Props extends Partial<Pick<Params, 'items' | 'order_by' | 'mime_types' | 'media_source'>> {
  brandKitId?: number
}

const fetchMediaFiles = (workspaceId: number, brandKitId: number, params: Params) => {
  const stringifiedParams = qs.stringify(params)
  return sendToApi<Data>(`/w/${workspaceId}/brand_kits/${brandKitId}/media_files?${stringifiedParams}`)
}

export const useInfiniteBrandKitMediaFiles = (
  { brandKitId, items = 20, order_by, mime_types, media_source }: Props,
  options?: UseInfiniteQueryOptions<Response<Data>>,
) => {
  const currentWorkspaceId = useCurrentWorkspaceId()

  return useInfiniteQuery<Response<Data>>(
    [
      'workspaces',
      currentWorkspaceId,
      'brandKits',
      brandKitId,
      'mediaFiles',
      'list',
      order_by,
      mime_types,
      media_source,
    ],
    ({ pageParam = 1 }) => {
      if (!brandKitId) {
        throw new Error('brandKitId is required')
      }
      return fetchMediaFiles(currentWorkspaceId, brandKitId, {
        page: pageParam,
        items,
        order_by,
        mime_types,
        media_source,
      })
    },
    {
      refetchOnWindowFocus: false,
      getNextPageParam,
      onError: (err) => {
        reportError(err, {
          severity: 'error',
        })
      },
      enabled: !!brandKitId,
      ...options,
    },
  )
}
